/* @flow */
import React, { useContext } from "react";
import {
  withColorsProvider,
  type ColorSet,
} from "nx-components/ColorsProvider";
import Badge from "nx-components/Badge";
import Button from "nx-components/Button";
import ItemList from "nx-components/ItemList";
import RichLabel from "nx-components/RichLabel";
import Theme from "nx-components/Theme";
import {
  IconGrid,
  IconWarning,
  IconListUl,
  IconExit,
  IconUser,
  IconSettings,
  IconSupport,
  IconBuilding,
} from "nx-components/Icons";
import AppState from "nexia_mobile/models/app_state";
import LocalSettings from "nexia_mobile/models/local_settings";
import BackboneListener from "nexia_mobile/components/BackboneListener";
import MainMenuBrandLogo from "nexia_mobile/components/BrandLogo/MainMenuBrandLogo";
import PivotMainMenuBrandLogo from "nexia_mobile/components/BrandLogo/PivotMainMenuBrandLogo";
import connect from "nexia_mobile/flux_like/store/connect";
import Dispatch from "nexia_mobile/flux_like/dispatch";
import Environment from "nexia_mobile/environment";
import ScrollableDiv from "nx-components/ScrollableDiv";
import Session from "nexia_mobile/models/session";
import { history, clearHistoryAndPush } from "nexia_mobile/history";
import { getTransitionPromise } from "nexia_mobile/components/Routing/HistoryPageTransitionController";
import UserLanguageContext from "nx-components/ContextWrappers/UserLanguageContext";

type Props = {
  onItemSelected: () => void,
  itemIsSelected: (id: string) => boolean,
  showAlertsBadge?: ?boolean,
  session: Session,
  appState: AppState,
  colors: ColorSet,
};

const MainMenuWithoutColor = (props: Props) => {
  const languageContext = useContext(UserLanguageContext);
  const language = languageContext.localeSet.nexia_mobile.MainMenuWithoutColor;
  const {
    onItemSelected,
    itemIsSelected,
    showAlertsBadge,
    session,
    appState,
    colors,
  } = props;

  const styles = {
    item: {
      padding: "0 1em",
      cursor: "pointer",
    },
    disabledItem: {
      opacity: "0.25",
    },
    buttonGroup: {
      padding: "1em",
    },
    button: {
      marginBottom: "0.5em",
    },
    iconStyle: {
      fontSize: "1em",
    },
    badgeStyle: {
      width: "12px",
      height: "12px",
      backgroundColor: colors.RED,
    },
    badgeWrapper: {
      position: "relative",
    },
    uaText: {
      fontSize: "0.7em",
      paddingTop: "1em",
      paddingLeft: "2em",
      paddingRight: "2em",
    },
    uaTextBold: {
      fontWeight: "bold",
    },
  };

  return (
    <BackboneListener listenTo={session} events={["change"]}>
      {() => (
        <BackboneListener
          listenTo={appState}
          events={["change:inDemoMode", "change:currentLocation"]}
        >
          {() => {
            const { hasAlerts } = session;
            const isActivatedByActivationCode = session.get(
              "isActivatedByActivationCode"
            );
            const { inDemoMode } = appState;
            const isCommercial = Environment.isCommercial();
            const authPinUser = LocalSettings.isEnabledByAuthPin();
            const isAtBuildingDashboard = appState.currentLocation === null;
            const hasMultipleLocations = session.hasMultipleLocations();

            const isEventItemSelected = () => {
              return isAtBuildingDashboard ? false : itemIsSelected("events");
            };

            const isEventItemDisabled = () => {
              return isAtBuildingDashboard;
            };

            let alertIcon;

            if (showAlertsBadge) {
              alertIcon = (
                <div style={styles.badgeWrapper}>
                  <IconWarning />
                  <Badge style={styles.badgeStyle} />
                </div>
              );
            } else {
              alertIcon = <IconWarning />;
            }
            let items = [
              {
                id: "home",
                icon: <IconGrid />,
                label: language.dashboardLabel,
                selected: itemIsSelected("home"),
                onTap: () => {
                  if (hasMultipleLocations) {
                    appState.resetCurrentLocation();
                  }
                  if (!itemIsSelected("home")) {
                    clearHistoryAndPush("/app/home");
                  }
                  onItemSelected();
                },
              },
            ];

            items.push({
              id: "alerts",
              icon: alertIcon,
              label: language.alertsLabel,
              selected: itemIsSelected("alerts"),
              onTap: () => {
                if (!itemIsSelected("alerts")) {
                  clearHistoryAndPush("/app/alerts");
                }
              onItemSelected();
              },
            });
            items.push({
              id: "events",
              icon: <IconListUl />,
              label: language.eventsLabel,
              selected: isEventItemSelected(),
              style: isAtBuildingDashboard ? styles.disabledItem : null,
              onTap: () => {
                if (isAtBuildingDashboard) {
                  return;
                }
                if (!itemIsSelected("events")) {
                  clearHistoryAndPush("/app/events");
                }
                onItemSelected();
              },
              noActiveStyle: isEventItemDisabled(),
            });
            if (!authPinUser && !inDemoMode) {
              items.push({
                id: "users",
                icon: <IconUser />,
                label: language.usersLabel,
                selected: itemIsSelected("users"),
                onTap: () => {
                  onItemSelected();
                  if (itemIsSelected("users")) {
                    return;
                  }
                  if (isActivatedByActivationCode != null) {
                    clearHistoryAndPush("/app/phones");
                  } else {
                    // A commercial account with isActivatedByActivationCode as either null or 1
                    history.push("/reauth", {
                      authenticationParams: { authenticationFor: "users" },
                      prompt: language.usernamePasswordPrompt,
                      title: language.manageUsersTitle,
                      onUserAuthenticated: () => {
                        // rather than fetch the session, manually set the expected result to avoid side effects
                        session.set("isActivatedByActivationCode", 0);
                        clearHistoryAndPush("/app/phones");
                      },
                    });
                  }
                },
              });
              items.push({
                id: "managebuilding",
                icon: <IconBuilding />,
                label: language.buildingConfigurationLabel,
                selected: itemIsSelected("managebuilding"),
                onTap: () => {
                  onItemSelected();
                  if (itemIsSelected("managebuilding")) {
                    return;
                  }
                  clearHistoryAndPush("/app/buildings");
                },
              });
            }
            if (inDemoMode) {
              items.push({
                id: "exit-demo",
                icon: <IconExit />,
                label: language.exitDemoLabel,
                selected: itemIsSelected("exit-demo"),
                onTap: () => {
                  Dispatch("APP_LOG_OUT");
                  onItemSelected();
                },
              });
            }
            if (!inDemoMode) {
              items.push(
                {
                  id: "settings",
                  icon: <IconSettings />,
                  label: language.appSettingsLabel,
                  selected: itemIsSelected("settings"),
                  onTap: () => {
                    if (!itemIsSelected("settings")) {
                      clearHistoryAndPush("/app/settings");
                    }
                    onItemSelected();
                  },
                },
                {
                  id: "support",
                  icon: <IconSupport />,
                  label: language.supportAndFeedbackLabel,
                  selected: itemIsSelected("support"),
                  onTap: () => {
                    if (!itemIsSelected("support")) {
                      clearHistoryAndPush("/app/support");
                    }
                    onItemSelected();
                  },
                }
              );
            }

            items = items.map((item) => {
              return {
                id: item.id,
                label: (
                  <RichLabel
                    label={<span>{item.label}</span>}
                    icon={item.icon}
                    iconStyle={styles.iconStyle}
                  />
                ),
                selected: item.selected,
                style: {
                  ...styles.item,
                  ...(item.style ? item.style : null),
                },
                onTap: item.onTap,
                activeStyle: item.noActiveStyle
                  ? undefined
                  : {
                      color: colors.PRIMARY,
                    },
                holdActiveUntil: getTransitionPromise,
              };
            });

            return (
              <ScrollableDiv
                elementType="nav"
                data-component-name="MainMenu"
                // This gets focused by DrawerRevealer
                tabIndex="-1"
                style={{
                  paddingTop: Theme.Padding.SAFE_AREA_TOP,
                  paddingLeft: Theme.Padding.SAFE_AREA_LEFT,
                  paddingBottom: Theme.Padding.SAFE_AREA_BOTTOM,
                }}
              >
                {isCommercial ? (
                  <PivotMainMenuBrandLogo />
                ) : (
                  <MainMenuBrandLogo />
                )}
                <ItemList id="menu-items" items={items} />
                {inDemoMode ? null : (
                  <div>
                    {isAtBuildingDashboard ? (
                      <div style={styles.uaText}>
                        <div>
                          <span
                            style={styles.uaTextBold}
                          >{`${language.note}: `}</span>
                          {language.selectBuldingToEnable}
                        </div>
                      </div>
                    ) : null}
                    <div style={styles.buttonGroup}>
                      <Button
                        theme="outline"
                        disabled={isAtBuildingDashboard}
                        style={styles.button}
                        full
                        onTap={() => {
                          clearHistoryAndPush("/app/groups/add");
                          onItemSelected();
                        }}
                      >
                        {language.createGroupButton}
                      </Button>
                      <Button
                        theme="outline"
                        disabled={isAtBuildingDashboard}
                        style={styles.button}
                        full
                        onTap={() => {
                          clearHistoryAndPush("/app/devices/add");
                          onItemSelected();
                        }}
                      >
                        {language.enrollNewDeviceButton}
                      </Button>
                    </div>
                  </div>
                )}
              </ScrollableDiv>
            );
          }}
        </BackboneListener>
      )}
    </BackboneListener>
  );
};

export const MainMenu = withColorsProvider(MainMenuWithoutColor);

export default connect(
  {
    showAlertsBadge: "showAlertsBadge",
    session: "session",
    appState: "appState",
  },
  MainMenu
);



// WEBPACK FOOTER //
// ./src/components/MainMenu.jsx